import { IIntervalService, IRootScopeService, ITimeoutService } from 'angular';
import { Cesium } from 'app/cesium';
import { ConfigService } from 'app/config/config.service';
import { ImsObject } from 'app/shared/collections/ims_object';
import { AbstractFetcher } from 'app/shared/collections/time/fetcher/fetcher.factory';
import { Processor } from 'app/shared/collections/time/processor/Processor';
import { AbstractQueue } from 'app/shared/collections/time/queue/queue.factory';
import { WITH_LOCATION_ENDPOINT_FUNC } from 'app/shared/resources';
import { CollectionCRUDRunner } from 'app/shared/store_and_forward/CRUDRunner';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { julianToUnix } from 'app/shared/utilities/time';
import { ViewStateService } from 'app/shared/view_state.service';
import { StartupRegistry } from 'app/startup/registry/registry';
import { VectorHeatMapContext } from 'app/views/view_types/view_utils';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import DebugService from '../../debug.service';
import { EventRegistry } from '../../events/event_registry';
import { SiteStateService } from '../../site_state.service';
import ImsObjectCollection from '../ims_object_collection.service';
import { TimeIntervalChunker } from './queue/time_interval_chunker.factory';
import { ILocalStorageService } from 'angular-local-storage';

const log = (...args) => DebugService.anonymous('Collections', ...args);

const TIME_LISTENER_THROTTLE = ConfigService.statCollectionRefreshThrottle;
const REQUEST_LATEST_STAT_THROTTLE = 10000;
const TIMELINE_MOVED_EVENT_THROTTLE_IN_SECONDS = ConfigService.timelineMinutes * 60;
const NO_SITE_ERROR_MSG = 'No Site';
const COLLECTION_STOPPED_ERROR_MSG = 'Collection has been stopped';

export interface ImsTimeCollectionOptions {
    requestLatestStats?: boolean;
    fetchWaitTime?: number;
    requestLatestStatThrottle?: number;
    crudRunner?: CollectionCRUDRunner;
}

// TODO: maybe merge this with HeatMapOptions sometime in the future
export interface GetWithLocationFilterArgs {
    startTime?: string;
    endTime?: string;
    siteIds?: number[];
    assetIds?: number[];
    notAssetIds?: number[];
    assetTypeIds?: number[];
    ipDeviceIds?: number[];
    ipDeviceTypeIds?: number[];
    ipDeviceTagIds?: number[];
    // assetType.mobility to include
    assetTypeMobilityList?: number[];
    // If true, include stats with ignore_stat = true
    // If false, include stats with ignore_stat = false
    // If nil, include all stats, regardless of ignore_stat value
    ignoredStatFilterValue?: boolean;
    // The limit number of records to return. The API implements a default but
    // you should probably pass this in!
    limit?: number;
    // the amount of seconds on either side of the stat of interest
    // that we will search for a location stat. If the location stat
    // falls too far from the stat of interest, the stat of interest
    // is dropped!
    interpolationLimitSeconds?: number;
    // The enabled view Id
    viewId?: number;
    statFilters: any;
}

// This is the data that comes out of the API's with_location end points. Most stat
// end points will implement this method - the main exception at the moment being
// location stats!
export interface DataWithLocation {
    // location replaced by separate lat, lon
    lat: number;
    lon: number;

    // The rest of the stat info props remain the same
    id: number;
    sub_stat_id?: number;
    ip_device_id?: number;
    asset_id?: number;

    // This gets punched onto the object after the response has been returned from the API
    // It makes this dictionary look like a real stat object.
    imsType: string;

    // There will be other arbitrary props
}

/**
 * The whole data structure that comes out of the with_location end point.
 */
export type DataWithLocationArray = DataWithLocation[];

export class ImsTimeCollection<T extends ImsObject = ImsObject> extends ImsObjectCollection {
    private queue: AbstractQueue;
    private fetcher: AbstractFetcher;
    private requestLatestStats: boolean;
    private fetchWaitTime: number;
    private rmTimeListener: () => void;
    private rmTimelineMovedListener: () => void;
    private rmSiteListener: () => void;
    private _max_id: number;
    private _$rootScope: IRootScopeService;
    private _$timeout: ITimeoutService;
    protected _clockHandler: ClockHandler;
    private _viewStateService: ViewStateService;
    private _siteStateService: SiteStateService;

    // TODO Move to a State Machine for Collections
    // Please someone turn this into a state machine for collections,
    // and then remove all the site and view listeners logic from inside the collection.
    private _isProcessing: boolean;
    protected _waitingForSite: boolean;
    protected _waitingForView: boolean;
    protected _isActive: boolean;

    // Event registry for the Collection Changed event
    protected _collectionChangedEventRegistry: EventRegistry;
    // Event registry for when the fetched / unfetched intervals update
    private _intervalsUpdatedEventRegistry: EventRegistry;
    // This is a throttled function to call instead of spamming the actual getting of earliest stats
    private _throttledGetLatestStats: any;

    protected readonly processor: Processor<T>;

    constructor(
        startUpRegistry: StartupRegistry,
        localStorageService: ILocalStorageService,
        $rootScope: IRootScopeService,
        $timeout: ITimeoutService,
        $interval: IIntervalService,
        clockHandler: ClockHandler,
        viewStateService: ViewStateService,
        siteStateService: SiteStateService,
        resource,
        ims,
        queue: AbstractQueue,
        fetcher: AbstractFetcher,
        processor: Processor<T>,
        options: ImsTimeCollectionOptions = {},
    ) {
        super(
            startUpRegistry,
            localStorageService,
            $interval,
            resource,
            ims,
            Object.assign(
                {
                    refreshRate: null,
                    incremental: false,
                    onDemand: false,
                    runInit: false,
                },
                options,
            ),
        );

        const conformedOptions = Object.assign(
            {
                requestLatestStats: false,
                fetchWaitTime: 300,
                requestLatestStatThrottle: REQUEST_LATEST_STAT_THROTTLE,
            },
            options,
        );

        const { requestLatestStats, fetchWaitTime, requestLatestStatThrottle } = conformedOptions;

        this._max_id = 0;

        this._$rootScope = $rootScope;
        this._$timeout = $timeout;
        this._clockHandler = clockHandler;
        this._viewStateService = viewStateService;
        this._siteStateService = siteStateService;

        this.queue = queue;
        this.fetcher = fetcher;
        this.processor = processor;

        this.requestLatestStats = requestLatestStats;
        this.fetchWaitTime = fetchWaitTime;

        // TODO Move to a State Machine for Collections
        this._isProcessing = false;
        this._isActive = false;
        this._waitingForSite = true;
        this._waitingForView = true;

        // Event registry for the Collection Changed event
        this._collectionChangedEventRegistry = new EventRegistry();

        this._intervalsUpdatedEventRegistry = new EventRegistry();

        // This is a throttled function to call instead of spamming the actual getting of earliest stats
        this._throttledGetLatestStats = _.throttle(async (start) => {
            const params = {
                site_id: this._siteStateService.site.id,
                timestamp__lte: start.toISOString(),
                limit: 100000,
            };
            return this.requestLatestStatsBeforeTime(params);
        }, requestLatestStatThrottle);

        // At this stage, for the purposes of application life cycle control we don't need
        // to tightly control time collections. In fact, we take advantage of this because
        // collections for dynamic stats are created at a non-deterministic time as we need
        // to wait for the result of the dynamic stat schema collection.
        this.initialise();
        // VERY IMPORTANT: notify the start up registry!
        this.setReady();
    }

    initialise() {
        super.initialise();

        this._viewStateService.onViewSelectionChanged(() => {
            this._viewChanged();
        });
    }

    addOnChangedListener(callbackFunc) {
        const rm = this._collectionChangedEventRegistry.addEventListener(callbackFunc);
        // callback after the listener has been added in-case it's removed in the first invocation
        callbackFunc();
        return rm;
    }

    addOnIntervalsChangedListener(callbackFunc) {
        return this._intervalsUpdatedEventRegistry.addEventListener(callbackFunc);
    }

    async getWithLocation(
        columnName: string, // Name of the stat, ie; "latency"
        filterArgs: GetWithLocationFilterArgs,
        limit: number,
        vectorHeatMapContext: VectorHeatMapContext | null,
    ): Promise<DataWithLocation | any> {
        // Every stat table behind an ims time collection class should implement the necessary with location func
        // on the resource object.
        // There is one exception - location stats. This will fail gloriously if the programmer attempts to
        // use the location stat collection.
        if (!this.resourceObjectBase[WITH_LOCATION_ENDPOINT_FUNC]) {
            throw new Error(`No with location func found on resource object`);
        }

        const queryParams = {
            column_name: columnName,
            timestamp__gte: filterArgs.startTime,
            timestamp__lte: filterArgs.endTime,
            // start id filters here...
            site_ids: filterArgs.siteIds,
            asset_ids: filterArgs.assetIds,
            not_asset_ids: filterArgs.notAssetIds,
            ip_device_ids: filterArgs.ipDeviceIds,
            asset_type_ids: filterArgs.assetTypeIds,
            ip_device_type_ids: filterArgs.ipDeviceTypeIds,
            ip_device_tag_ids: filterArgs.ipDeviceTagIds,
            asset_type_mobility_list: filterArgs.assetTypeMobilityList,
            limit: limit,
            interpolation_limit_seconds: filterArgs.interpolationLimitSeconds,
            view_id: filterArgs.viewId,
            filters: filterArgs.statFilters,
            azimuth_column_name: vectorHeatMapContext?.azimuthColumnName,
            elevation_column_name: vectorHeatMapContext?.elevationColumnName,
        };

        if (!_.isNil(filterArgs.ignoredStatFilterValue)) {
            queryParams['ignored_stat_filter_value'] = filterArgs.ignoredStatFilterValue;
        }

        try {
            const imsType = this.getImsType();
            const response =
                await this.resourceObjectBase[WITH_LOCATION_ENDPOINT_FUNC](queryParams).$promise;

            return response.reduce((accum, obj) => {
                // It's possible for bad locations to make it to the database.
                // Protect ourselves!
                if (isLocationValid(obj.lat, obj.lon)) {
                    // Mutate each object with the imsType
                    obj.imsType = imsType;
                    // location ok, keep it
                    accum.push(obj);
                }
                return accum;
            }, []);
        } catch (e) {
            if (e.status === -1) {
                throw new Error('connection to server timed out');
            }
            if (e.status >= 400) {
                const errorJson = e.data;
                if (errorJson && errorJson.message) {
                    throw new Error(errorJson.message);
                }
            }
            // Generic error
            throw new Error(`Failed to fetch data with status code ${e.status}`);
        }
    }

    _createListeners() {
        if (!this.rmTimeListener) {
            this.rmTimeListener = this._clockHandler.addOnTimelineMovedListener(
                _.throttle(
                    (startJulian, endJulian, cursorJulian, startTime, endTime, cursorTime) => {
                        this._newRange(startTime, endTime, startJulian, endJulian);
                    },
                    TIME_LISTENER_THROTTLE,
                ),
            );
        }
        if (!this.rmSiteListener) {
            this.rmSiteListener = this._siteStateService.onSiteSelectionChanged(() =>
                this._siteChanged(),
            );

            this._siteChanged();
        }

        // Handles getting the latest stats before the timeline as the timeline moves forward
        if (this.requestLatestStats && !this.rmTimelineMovedListener) {
            this.rmTimelineMovedListener = this._clockHandler.addOnTimelineMovedListener(() => {
                this._throttledGetLatestStats(this._clockHandler.getStartTimeAsMoment()).catch(
                    (err) => {
                        if (err.message === NO_SITE_ERROR_MSG) {
                            // This can reject if the current site is not yet set.
                            // Not to worry. Everything is OK.
                        } else {
                            return Promise.reject(err);
                        }
                    },
                );
            }, TIMELINE_MOVED_EVENT_THROTTLE_IN_SECONDS);
        }
    }

    _destroyListeners() {
        if (this.rmTimeListener) {
            this.rmTimeListener();
            this.rmTimeListener = null;
        }

        if (this.rmSiteListener) {
            this.rmSiteListener();
            this.rmSiteListener = null;
        }
        if (this.rmTimelineMovedListener) {
            this.rmTimelineMovedListener();
            this.rmTimelineMovedListener = null;
        }
    }

    refreshData(params = {}, checkTime = false, useInFlightCheck = false) {
        // STUB not implemented
        // super.refreshData(params, checkTime);
    }

    resetCollection() {
        super.resetCollection();

        this.processor.clear();

        // Notify everyone that the collection changed
        this._emitChange();
    }

    public get isActive(): boolean {
        return this._isActive;
    }

    _viewChanged() {
        this._waitingForView = false;
        const shouldBeActive =
            this._viewStateService.currentViewController.activeCollections.includes(
                this.constructor.name,
            );
        const changed = shouldBeActive !== this._isActive;
        this._isActive = shouldBeActive;
        if (changed && shouldBeActive) {
            // Create listeners for time change and site changes
            this._createListeners();
            // Tell it the current time with newRange(), this will start processing if need be
            if (this._clockHandler.clock) {
                this._newRange(
                    this._clockHandler.clock.getStartTimeAsMoment(),
                    this._clockHandler.clock.getEndTimeAsMoment(),
                    this._clockHandler.clock.getStartTimeAsJulian(),
                    this._clockHandler.clock.getEndTimeAsJulian(),
                );
            }
        } else if (changed && !shouldBeActive) {
            // Destroy listeners
            this._destroyListeners();
            // Empty the queue of everything (all intervals, all data, everything). This will stop any current processing as the queue will be empty
            this.queue.clear();
            // Clear the collection out
            this.resetCollection();
        }
    }

    _siteChanged() {
        this._waitingForSite = false;
        this.resetCollection();
        // Tell the queue that in it's current state, everything is now un-fetched
        this.queue.markAllAsUnfetched();
        // Tell the collection to get earliest stats again for the new site
        if (this._clockHandler.clock && this.requestLatestStats) {
            this._throttledGetLatestStats(this._clockHandler.getStartTimeAsMoment());
        }

        if (!this._isProcessing && !this._waitingForSite && !this._waitingForView) {
            this._getNextItem();
        }
    }

    /**
     * This function will be called by the time collection when a collection should update 'latest stats before a time'
     * @returns function to call that returns an object with $promise on it
     */
    getLatestStatsResourceFunction(): () => unknown {
        throw new Error(
            `requestLatestStatsBeforeTime method not implemented by ${this.constructor.name}, either override or don't request latest stats`,
        );
    }

    /**
     * Very similar to {@link ImsTimeCollection.requestLatestStatsBeforeTime}, accept it won't ditch the response
     * if the collection is not active. Think of it like this method is for public use, and requestLatestStatsBeforeTime
     * is for private use by the collection because it depends on the collection's internal state. It's fine.
     * Returned results will not be persisted in the collection.
     * @param params
     */
    public async collectLatestStats(params: any) {
        const latestStatsResourceFunction = this.getLatestStatsResourceFunction();
        const response = await this.fetcher.fetch(latestStatsResourceFunction, params, 0);
        return this._processResources(response);
    }

    /**
     * This function will be called by the time collection when a collection should update it 'latest stats before a time'
     * @param params the query parameters for the request
     * @returns {[*]} An array of [function to call that returns an object with $promise on it, parameter object to call the function with]
     */
    private async requestLatestStatsBeforeTime(params): Promise<any> {
        const latestStatsResourceFunction = this.getLatestStatsResourceFunction();
        return this._fetchItem(latestStatsResourceFunction, params, 0).catch((error) => {
            if (error && error.message === COLLECTION_STOPPED_ERROR_MSG) {
                // this is fine
                return;
            }
            // otherwise log it
            console.error(error);
        });
    }

    _processResources(resources) {
        const changed = {};
        for (const resource of resources) {
            changed[resource.id] = this.createImsObject(resource, this.lastUpdatedFromApi);
        }
        return changed;
    }

    async collectionChanged(changed, origin) {
        // Mark the collection as changed
        this.collectionNotInitialized = false;

        if (Object.keys(changed).length === 0) {
            return;
        }

        await this.processor.add(Object.values(changed));
        // and wait for all the listeners
        await this._emitChange();
    }

    _newRange(start, end, startJulian, endJulian) {
        const oldStart = this.queue.getStartTime();
        if (
            (this.requestLatestStats &&
                oldStart &&
                startJulian &&
                Cesium.JulianDate.lessThan(startJulian, oldStart)) ||
            (this.requestLatestStats && !oldStart)
        ) {
            // The queue moved backwards, make a throttled call to get latest data
            this._throttledGetLatestStats(start).catch((error) =>
                this._throttledGetLatestStats.cancel(),
            );
        }

        const interval = ImsTimeCollection.convertJuliansToInterval(startJulian, endJulian);
        this.queue.addInterval(interval, null, true, true);

        const paddedStart = this.queue.getStartTime();
        const paddedStop = this.queue.getStopTime();

        this.processor.remove(julianToUnix(paddedStart), julianToUnix(paddedStop));

        if (!this._isProcessing && !this._waitingForSite && !this._waitingForView) {
            this._getNextItem();
        }
    }

    defineRelationship() {
        throw new Error('not implemented for time collections');
    }

    getCurrentIntervals(): Cesium.TimeIntervalCollection {
        return this.queue.getIntervals(TimeIntervalChunker.FETCHED);
    }

    extendParams(params, isLiveChunk = undefined) {
        return params;
    }

    _getNextItem() {
        if (this.queue.isEmpty()) {
            this._isProcessing = false;
            log(`${this.constructor.name} queue complete`);
            return Promise.resolve();
        }

        if (!this.isActive) {
            // somehow the queue isn't empty but we're not active.
            // this is probably because of a timeout has eventually called this method,
            // and in between that time the collection was stopped.
            // just clear this thing out again, it'll be fine I hope.
            this.queue.clear();
            this._isProcessing = false;
            return Promise.resolve();
        }

        // The queue isn't empty
        const cursorTime = this._clockHandler.getCursorTimeAsJulian();
        const latestTime = ImsTimeCollection.convertMomentToJulian(
            this._clockHandler.latestMoment(),
        );
        const interval = this.queue.nextItem(cursorTime, latestTime);
        // If we can grab the next item, fetch it
        if (interval) {
            this._isProcessing = true;
            const [func, params, delay] = this._setupItemToFetch(interval, latestTime);
            return this._fetchItem(func, params, delay)
                .then(() => {
                    // Mark the interval as fetched
                    this.queue.addInterval(interval, TimeIntervalChunker.FETCHED, false, false);
                })
                .catch((error) => {
                    if (error && error.message === COLLECTION_STOPPED_ERROR_MSG) {
                        // this chunk has been ignored, just skip this and continue, we'll be fine trust me
                        return Promise.resolve();
                    }
                    // Mark the interval as failed
                    this.queue.addInterval(interval, TimeIntervalChunker.ERROR, false, false);
                })
                .then(() => this._intervalsUpdatedEventRegistry.notifyListeners()) // interval queues have been updated - tell the world
                .then(() => {
                    if (this.fetchWaitTime) {
                        // wait a little bit before fetching the next item
                        return this._$timeout(this.fetchWaitTime, false).then(() =>
                            this._getNextItem(),
                        );
                    }
                    // or go straight for it!
                    return this._getNextItem();
                });
        } else {
            // The queue is NOT empty, but we can't grab the item yet.
            // After a while, try getting the item again.
            return this._$timeout(() => this._getNextItem(), TIME_LISTENER_THROTTLE);
        }
    }

    getStats(lookup: number, time: Cesium.JulianDate): Array<T> {
        return this.processor.get(lookup, julianToUnix(time));
    }

    getStatsUnix(lookup: number, time: number): Array<T> {
        return this.processor.get(lookup, time);
    }

    getStatCount(lookup: number): number {
        return this.getAllStatsForKey(lookup).length;
    }

    getAllStatsForKey(lookup: number): Array<T> {
        return this.processor.getAllForKey(lookup); //Filter all stats returned by Subgroup
    }

    getAllStats(): Array<T> {
        return this.processor.getAll();
    }

    _setupItemToFetch(interval, latestTime) {
        const [start, end] = ImsTimeCollection.convertIntervalToMoments(interval);
        // If the collection has intervals, use the end of the intervals, otherwise work out from the queue what's fetched and use the end of that, or if the queue hasn't fetched anything, use the start of the queue
        // const latestFetchedTime = this.intervals.stop || this.queue.getIntervals(TimeIntervalChunker.FETCHED).stop || this.queue.getStartTime();
        const latestFetchedTime =
            this.queue.getIntervals(TimeIntervalChunker.FETCHED).stop || this.queue.getStartTime();
        // Generate the parameters, then ask the collection to extend them.
        const func = this.resourceObjectBase.query;
        const endJulian = Cesium.JulianDate.fromDate(end.toDate());
        const isLiveChunk =
            latestFetchedTime &&
            Cesium.JulianDate.lessThan(latestFetchedTime, endJulian) &&
            Math.abs(Cesium.JulianDate.secondsDifference(latestTime, endJulian)) < 3;
        const params = this.extendParams(
            ImsTimeCollection.generateParams(
                start,
                end,
                latestTime,
                latestFetchedTime,
                this._max_id,
                this._siteStateService.site.id,
                isLiveChunk,
            ),
            isLiveChunk,
        );
        const delay = 0;
        return [func, params, delay];
    }

    async _fetchItem(func, params, delay) {
        return this.fetcher.fetch(func, params, delay).then(async (response) => {
            if (!this.isActive) {
                // we're no longer active, throw this thing away!
                return Promise.reject(new Error(COLLECTION_STOPPED_ERROR_MSG));
            }
            return await this._processResponse(response);
        });
    }

    async _processResponse(response) {
        const changed = this._processResources(response);

        await this.collectionChanged(changed, this.constructor.name);

        this._max_id = response.length ? Math.max(0, ...response.map((i) => i.id)) : this._max_id;

        return changed;
    }

    _emitChange() {
        return this._collectionChangedEventRegistry.notifyListeners();
    }

    static generateParams(
        start,
        end,
        latestTime,
        latestIntervalTime,
        max_id,
        siteId,
        isLiveChunk,
    ): any {
        if (isLiveChunk) {
            // If this is not the first interval, and we're grabbing the latest interval yet, and that interval is very close to live.
            // Increase the end timestamp by 10 minutes to catch a server that's time is set ahead of the front-end
            return {
                timestamp__gte: start.subtract(5, 'minutes').toISOString(),
                timestamp__lt: end.add(10, 'minutes').toISOString(),
                id__gt: max_id,
                limit: 100000,
                site_id: siteId,
            };
        }
        return {
            timestamp__gte: start.toISOString(),
            timestamp__lt: end.toISOString(),
            limit: 100000,
            site_id: siteId,
        };
    }

    static convertIntervalToMoments(interval) {
        return [
            moment(Cesium.JulianDate.toDate(interval.start)),
            moment(Cesium.JulianDate.toDate(interval.stop)),
        ];
    }

    static convertJuliansToInterval(startJulian, endJulian) {
        return new Cesium.TimeInterval({
            start: startJulian,
            stop: endJulian,
            isStartIncluded: true,
            isStopIncluded: true,
            data: null,
        });
    }

    static convertMomentsToInterval(startMoment, endMoment) {
        return new Cesium.TimeInterval({
            start: Cesium.JulianDate.fromDate(startMoment.toDate()),
            stop: Cesium.JulianDate.fromDate(endMoment.toDate()),
            isStartIncluded: true,
            isStopIncluded: true,
            data: null,
        });
    }

    static convertMomentToJulian(time) {
        return Cesium.JulianDate.fromDate(time.toDate());
    }
}

function isLocationValid(lat: number, lon: number): boolean {
    return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}
